import React from 'react'
import {
  Button,
  Checkbox,
  Group,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core'
import type { ContextModalProps } from '@mantine/modals'
import { useProviders } from '@/components/filters/providers/use-providers.ts'
import { Icon } from '@/components/lib/Icon'
import type { CreateTerrapinParams } from '@/hooks/use-terrapin'
import {
  useLocationTypeFilter,
  useSetProviders,
  useTimePresetFilter,
} from '@/stores/filters-store.ts'
import { useFeature } from 'featurehub-react-sdk'
import { ConfigureImageOptions } from './ConfigureImageOptions'
import { ConfigureTimings } from './ConfigureTimings'
import {
  ConfigurationTableProps as tableProps,
  useConfigureSavedSearch,
} from './use-configure-saved-search'

export interface SaveSearchSettingsModalParams {
  params?: CreateTerrapinParams
  refId?: string
  isNew?: boolean
}

enum TabTitle {
  PROVIDER_SELECTOR = 'provider-selector',
  IMAGE_OPTIONS = 'image-options',
}

const TABS = {
  PROVIDER_SELECTOR: TabTitle.PROVIDER_SELECTOR,
  IMAGE_OPTIONS: TabTitle.IMAGE_OPTIONS,
} as const

export function SaveSearchModal({
  context,
  id,
  innerProps: { refId, params: savedConfig },
}: ContextModalProps<SaveSearchSettingsModalParams>) {
  const {
    queryString,
    setQueryString,
    name,
    setName,
    isSaving,
    handleSave,
    expirationDate,
    setExpirationDate,
    emailNotification,
    setEmailNotification,
    backfill,
    setBackfill,
  } = useConfigureSavedSearch({ savedConfig, refId })

  const timePreset = useTimePresetFilter()
  const [activeTab, setActiveTab] = React.useState<TabTitle>(
    TABS.PROVIDER_SELECTOR,
  )

  const { providers, selectedProviders } = useProviders()

  const providerLabels = Object.entries(providers).map(([value, label]) => ({
    value,
    label,
  }))

  const setProviders = useSetProviders()

  const updateProviders = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (selectedProviders.includes(value)) {
      setProviders(selectedProviders.filter((p) => p !== value))
    } else {
      setProviders([...selectedProviders, value])
    }
  }

  function closeModal() {
    context.closeModal(id)
  }
  const noBackfillTimeSelected = backfill && timePreset === 'None'
  const isFeed = savedConfig?.mode === 'feed'
  const isSaveDisabled =
    !name || !queryString || isSaving || noBackfillTimeSelected
  const isPolygonSaveDisabled = !name || isSaving
  const isPolygonSearch = useLocationTypeFilter() === 'polygon'
  const showDynamicProviders = useFeature('ENABLE_DYNAMIC_PROVIDERS')

  return (
    <>
      <Stack gap="md">
        {!isPolygonSearch && (
          <TextInput
            leftSection={<Icon name="search" />}
            label="Search query"
            value={queryString}
            onChange={(event) => setQueryString(event.target.value)}
            placeholder="Enter the search query for your feed"
            pattern="^[0-9a-zA-Z]{1,}"
            data-cy="save-search-query"
            required
            withAsterisk
          />
        )}
        <TextInput
          label="Name of this feed"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={savedConfig?.query}
          data-cy="save-search-name"
          required
          withAsterisk
        />
        {isFeed && (
          <>
            {showDynamicProviders ? (
              <Tabs value={activeTab} activateTabWithKeyboard={false}>
                <Tabs.Panel value={TABS.PROVIDER_SELECTOR}>
                  <Text size="sm" fw="bold" mb="xs">
                    Include the following providers in your search
                  </Text>
                  <SimpleGrid
                    cols={{ base: 2, sm: 3, lg: 4 }}
                    spacing={6}
                    style={{
                      border: '1px solid var(--mantine-color-gray-3)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      alignItems: 'start',
                      padding: '10px',
                      maxHeight: '200px',
                      overflowY: 'scroll',
                    }}
                  >
                    {providerLabels.map((provider) => (
                      <Checkbox
                        checked={selectedProviders.includes(provider.value)}
                        value={provider.value}
                        label={provider.label}
                        key={provider.value}
                        onChange={updateProviders}
                      >
                        {provider.value}
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                  <Button
                    size="sm"
                    mt="sm"
                    onClick={() => setActiveTab(TABS.IMAGE_OPTIONS)}
                  >
                    Image options
                  </Button>
                </Tabs.Panel>
                <Tabs.Panel value="image-options">
                  <Button
                    variant="transparent"
                    p={0}
                    c="gray"
                    leftSection={<Icon name="arrow_circle_left" />}
                    onClick={() => setActiveTab(TABS.PROVIDER_SELECTOR)}
                    title="Back"
                  >
                    Back to providers
                  </Button>
                  <ConfigureImageOptions tableProps={tableProps} />
                </Tabs.Panel>
              </Tabs>
            ) : (
              <ConfigureImageOptions tableProps={tableProps} />
            )}

            <ConfigureTimings
              savedConfig={savedConfig}
              tableProps={tableProps}
              expirationDate={expirationDate}
              setExpirationDate={setExpirationDate}
              emailNotification={emailNotification}
              setEmailNotification={setEmailNotification}
              backfill={backfill}
              setBackfill={setBackfill}
            />
          </>
        )}
      </Stack>
      <Group justify="space-between" mt="md">
        <Button variant="subtle" onClick={closeModal}>
          CANCEL
        </Button>
        <Button
          loading={isSaving}
          disabled={isPolygonSearch ? isPolygonSaveDisabled : isSaveDisabled}
          onClick={() => handleSave(closeModal)}
        >
          SAVE
        </Button>
      </Group>
    </>
  )
}
